<template>
  <div class="tl">
    <Breadcrumbs main="Пользователи" title="Трансферы"/>

    <div class="container-fluid tl-container">

      <!-- ================== TOOLBAR ================== -->
      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingLogs }">
            <i class="fa fa-search tl-search__icon"></i>
            <input
                ref="search"
                type="search"
                class="tl-search__input"
                v-model="searchModel"
                placeholder="Поиск по имени пользователя…"
                @keydown.esc.prevent="clearSearch"
            />
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <b-dropdown v-if="typeOptions.length > 1" variant="link" class="tl-drop" no-caret menu-class="tl-drop__menu">
            <template #button-content>
              <span class="tl-drop__btn" :class="{ 'is-on': typeRefine !== 'all' }">
                <i class="fa fa-exchange"></i>
                <span class="tl-drop__btn-text">{{ typeRefine === 'all' ? 'Тип события' : typeRefine }}</span>
                <i class="fa fa-angle-down"></i>
              </span>
            </template>
            <b-dropdown-item @click="typeRefine = 'all'">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': typeRefine !== 'all' }"></i> Любой тип
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item v-for="type in typeOptions" :key="'t' + type" @click="typeRefine = type">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': typeRefine !== type }"></i>
              {{ type }}
            </b-dropdown-item>
          </b-dropdown>

          <div class="tl-spacer"></div>

          <button type="button" class="tl-icon-btn" @click="toggleSort"
                  v-b-tooltip.hover.bottom="sort === 'desc' ? 'Сначала старые' : 'Сначала новые'">
            <i class="fa" :class="sort === 'desc' ? 'fa-sort-amount-desc' : 'fa-sort-amount-asc'"></i>
          </button>

          <button type="button" class="tl-icon-btn" :class="{ 'is-on': density === 'compact' }"
                  @click="toggleDensity" v-b-tooltip.hover.bottom="'Плотность строк'">
            <i class="fa fa-bars"></i>
          </button>

          <router-link :to="{ name: 'users_real' }" class="tl-icon-btn"
                       v-b-tooltip.hover.bottom="'Реальные пользователи'">
            <i class="fa fa-users"></i>
          </router-link>

          <button type="button" class="tl-icon-btn" :class="{ 'is-spin': isLoadingLogs }"
                  @click="refresh" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>
        </div>

        <div v-if="hasAnyRefine" class="tl-chips">
          <span class="tl-chips__label">Фильтры:</span>

          <button v-if="searchApplied" type="button" class="tl-chip" @click="clearSearch">
            <i class="fa fa-search"></i> «{{ searchApplied }}» <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button v-if="typeRefine !== 'all'" type="button" class="tl-chip tl-chip--local" @click="typeRefine = 'all'">
            <i class="fa fa-exchange"></i> {{ typeRefine }}
            <span class="tl-chip__hint">на странице</span>
            <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button type="button" class="tl-chips__reset" @click="resetAll">Сбросить всё</button>
        </div>
      </div>

      <!-- ================== CONTENT ================== -->
      <div class="tl-panel" :class="['tl-panel--' + density]">

        <div v-if="isLoadingLogs" class="tl-skeleton">
          <div v-for="n in 8" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--avatar"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-exchange"></i></div>
          <div class="tl-empty__title">
            {{ hasAnyRefine ? 'Ничего не нашлось' : 'Трансферов нет' }}
          </div>
          <div class="tl-empty__text">
            {{ hasAnyRefine
              ? 'Поиск работает по имени пользователя.'
              : 'Здесь появятся переходы игроков между командами.' }}
          </div>
          <div v-if="hasAnyRefine" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost-2" @click="resetAll">Сбросить фильтры</button>
          </div>
        </div>

        <div v-else class="tl-table-wrap">
          <b-table
              ref="table"
              class="tl-table"
              responsive
              hover
              borderless
              show-empty
              :items="rows"
              :fields="fields"
          >
            <template #cell(id)="{ item }">
              <span class="tl-id tl-id--sm">#{{ item.id }}</span>
            </template>

            <template #cell(date)="{ item }">
              <div class="tl-time">
                <span class="tl-time__abs">{{ formatDateTime(item.created_at) }}</span>
                <span class="tl-time__rel">{{ formatRelative(item.created_at) }}</span>
              </div>
            </template>

            <template #cell(type)="{ item }">
              <span class="tl-tag">{{ item.x_type || '—' }}</span>
            </template>

            <template #cell(description)="{ item }">
              <div class="tl-log">{{ item.x_description || '—' }}</div>
            </template>

            <template #cell(who)="{ item }">
              <span class="tl-muted-strong">{{ item.x_author || '—' }}</span>
            </template>

            <template #empty>
              <div class="tl-empty tl-empty--inline">Ничего не найдено</div>
            </template>
          </b-table>
        </div>

        <div v-if="!isLoadingLogs && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ total || 0 }}</b>
            <span v-if="typeRefine !== 'all'" class="tl-muted"> · отфильтровано на странице</span>
          </div>
          <b-pagination
              class="tl-pagination my-0"
              v-model="pageModel"
              :total-rows="total"
              :per-page="perPage"
              :limit="7"
              @change="goToPage"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/api/api";
import {getLogData} from "@/common/userLogs";
import {CODES} from "@/constants/User/userLogs";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";

const LS = {density: "userTransferLogsDensity"};

export default {
  name: 'UserTransferLog',
  mixins: [PaginationHelperMixin],
  data() {
    return {
      logsPageKey: 'userTransferLogsPageNumber',
      perPage: 10,
      page: 1,
      total: 0,

      logs: [],
      isLoadingLogs: false,

      searchModel: '',
      searchApplied: '',
      searchTimer: null,

      typeRefine: 'all',
      sort: 'desc',
      density: 'cozy',

      fields: [
        {key: 'id', label: 'ID'},
        {key: 'date', label: 'Дата'},
        {key: 'type', label: 'Тип'},
        {key: 'description', label: 'Описание'},
        {key: 'who', label: 'Пользователь'},
      ],
    }
  },
  computed: {
    pageModel: {
      get() {
        return Number(this.page) || 1;
      },
      set(value) {
        this.page = value;
      },
    },

    hasAnyRefine() {
      return Boolean(this.searchApplied) || this.typeRefine !== 'all';
    },

    /** Типы событий, встретившиеся на текущей странице — фильтр локальный. */
    typeOptions() {
      const types = new Set();
      (this.logs || []).forEach(log => {
        const code = log.type && log.type.code;
        if (code) types.add(code);
      });
      return Array.from(types).sort();
    },

    rows() {
      const list = (this.logs || []).map(log => ({
        ...log,
        x_type: (log.type && log.type.code) || '',
        x_author: (log.author && log.author.username) || '',
        x_description: this.describe(log),
      }));

      if (this.typeRefine === 'all') return list;
      return list.filter(log => log.x_type === this.typeRefine);
    },

    rangeFrom() {
      if (!this.total) return 0;
      return (this.pageModel - 1) * this.perPage + 1;
    },

    rangeTo() {
      return Math.min(this.pageModel * this.perPage, this.total || 0);
    },
  },
  watch: {
    searchModel(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.searchApplied === value.trim()) return;
        this.searchApplied = value.trim();
        this.page = 1;
        this.fetchLogs();
      }, 400);
    },
  },
  mounted() {
    this.restorePreferences();
    this.page = Number(this.getCurrentPageNumber(this.logsPageKey)) || 1;
    this.fetchLogs();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchLogs() {
      this.isLoadingLogs = true;
      this.recordPageToStorage(this.pageModel, this.logsPageKey);
      try {
        const response = await api.users.userTransferLogs({
          page: this.pageModel,
          search: this.searchApplied || undefined,
          sort: this.sort === 'desc' ? '-id' : undefined,
        });
        this.logs = response.data.user_transfer_logs || [];
        this.total = (response.data.pagination && response.data.pagination.total) || 0;
      } catch (e) {
        this.$toasted.error("Не удалось загрузить трансферы", {position: 'top-right', duration: 3000});
      } finally {
        this.isLoadingLogs = false;
      }
    },

    refresh() {
      this.fetchLogs();
    },

    goToPage(pageNumber) {
      this.page = pageNumber;
      this.$nextTick(this.fetchLogs);
    },

    toggleSort() {
      this.sort = this.sort === 'desc' ? 'asc' : 'desc';
      this.page = 1;
      this.fetchLogs();
    },

    clearSearch() {
      this.searchModel = '';
      if (!this.searchApplied) return;
      this.searchApplied = '';
      this.page = 1;
      this.fetchLogs();
    },

    resetAll() {
      this.searchModel = '';
      this.searchApplied = '';
      this.typeRefine = 'all';
      this.page = 1;
      this.fetchLogs();
    },

    describe(log) {
      const code = log.type && log.type.code;
      if (!code) return '';
      try {
        const message = getLogData({item: log})[code];
        if (message === undefined || message === null || message === '') return CODES[code] || '';
        return String(message);
      } catch (e) {
        return CODES[code] || '';
      }
    },

    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "—";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    formatRelative(value) {
      if (!value) return "";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "";
      const minutes = Math.round((Date.now() - date.getTime()) / 60000);
      if (minutes < 1) return "только что";
      if (minutes < 60) return `${minutes} мин. назад`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `${hours} ч. назад`;
      const days = Math.round(hours / 24);
      if (days < 30) return `${days} дн. назад`;
      return `${Math.round(days / 30)} мес. назад`;
    },

    restorePreferences() {
      try {
        const density = localStorage.getItem(LS.density);
        if (density === "compact" || density === "cozy") this.density = density;
      } catch (e) { /* повреждённые настройки — берём значения по умолчанию */
      }
    },

    toggleDensity() {
      this.density = this.density === "compact" ? "cozy" : "compact";
      localStorage.setItem(LS.density, this.density);
    },

    onKeydown(event) {
      const tag = (event.target && event.target.tagName) || "";
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag) || (event.target && event.target.isContentEditable);

      if (event.key === "Escape") {
        if (isTyping) return;
        if (this.hasAnyRefine) this.resetAll();
        return;
      }

      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";

.tl-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--tl-primary-soft);
  color: var(--tl-primary);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.tl-log {
  max-width: 560px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--tl-text);
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
